import React from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { Link } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import Navbar from "../../Landing/ForecastNestHome/navbar";
import Footer from "../../Landing/ForecastNestHome/footer";
import ResearchDisclaimer from "../../../Components/Common/ResearchDisclaimer";

/*
 * Cancellation and refunds.
 *
 * A merchant of record will not activate checkout without a page like this, so
 * one exists whatever the terms are.
 *
 * There is deliberately no money-back guarantee, and no self-serve refund.
 * An unconditional one is open to a scripted loop — subscribe, refund, repeat
 * — costing roughly US$1.26 a turn in Stripe fees that are not returned, and
 * every defence against that loop is either ineffective (blocking an account,
 * when a new account is free) or paid for by honest members (making them ask a
 * person for what they were promised). Selling a month at a time, cancellable
 * in two clicks, removes the surface instead of guarding it: the most anyone
 * can be out of pocket for something they have stopped wanting is one month.
 *
 * What is deliberately kept:
 *
 *   - A real address for problems, with no promise attached. Refusing every
 *     refund does not end disputes, it converts them into chargebacks, which
 *     cost far more than the payment and count against the payment account.
 *     A discretionary line is the cheap pressure valve; it is not a guarantee
 *     and must not be written as one.
 *   - The statutory-rights section. EU and UK consumers have a withdrawal right
 *     for digital purchases that a policy cannot sign away, and saying so is
 *     both accurate and better received than silence.
 */

// How long an account can sit unused before we may delete it, and how much
// warning is given first.
//
// Written as constants because the same two numbers appear in the privacy
// policy's retention section, and a refund policy that says 24 months beside a
// privacy policy that says something else is worse than either alone.
const DORMANT_MONTHS = 24;
const DORMANT_NOTICE_DAYS = 30;

const Section = ({ title, children }) => (
  <div className="d-flex mb-4">
    <div className="flex-shrink-0 me-3">
      <FeatherIcon icon="check-circle" className="text-success icon-dual-success icon-xs" />
    </div>
    <div className="flex-grow-1">
      <h5>{title}</h5>
      {children}
    </div>
  </div>
);

const RefundPolicy = () => {
  document.title = "Cancellation and Refund Policy | ForecastNest";

  return (
    <React.Fragment>
      <div className="layout-wrapper landing">
        <Navbar />
        <div className="section pt-5 mt-5">
          <Container>
            <Row className="justify-content-center">
              <Col lg={9}>
                <Card>
                  <CardBody className="p-4 p-lg-5">
                    <h4 className="mb-1">Cancellation and Refund Policy</h4>
                    <p className="text-muted mb-4">Last update: August 29, 2026</p>

                    <Section title="Cancel whenever you like">
                      <p className="text-muted">
                        Subscriptions are monthly and there is no contract, no minimum
                        term and no cancellation fee. Cancel from{" "}
                        <Link to="/profile">your profile</Link> at any time, in two
                        clicks, without talking to anyone.
                      </p>
                      <p className="text-muted mb-0">
                        Cancelling stops the next payment. It does not end the month you
                        have already paid for — you keep full access until that month
                        runs out, and then the account simply reverts to the free plan.
                        Nothing is deleted.
                      </p>
                    </Section>

                    <Section title="Payments already made">
                      <p className="text-muted">
                        We do not refund payments, including the unused part of a month
                        that is already underway. Cancelling before your renewal date is
                        how you avoid being charged again, and it is the only thing you
                        need to do.
                      </p>
                      <p className="text-muted mb-0">
                        Because billing is monthly, the most this can ever cost you for
                        a service you have stopped wanting is one month. We would rather
                        say that plainly than advertise a guarantee we would have to
                        police.
                      </p>
                    </Section>

                    <Section title="If something goes wrong">
                      <p className="text-muted mb-0">
                        This is a policy, not a wall. If you were charged twice, charged
                        after cancelling, or the service did not work and we could not
                        put it right, email{" "}
                        <a href="mailto:support@forecastnest.com">support@forecastnest.com</a>{" "}
                        from the address on your account and tell us what happened. We
                        read every one and we aim to reply within two business days.
                        Whether a refund is the right answer depends on the case — that
                        is a judgement rather than a promise, and this page will not
                        pretend otherwise.
                      </p>
                    </Section>

                    <Section title="What stays yours">
                      <p className="text-muted">
                        Cancelling ends the paid plan; it does not delete your research.
                        Everything you recorded stays in your account, and forecasts that
                        have already been scored keep their scores. If you want the data
                        removed as well, say so and we will delete it.
                      </p>
                      <p className="text-muted mb-0">
                        We do not keep it forever, though. An account that has not been
                        signed in to for {DORMANT_MONTHS} months may be deleted along with
                        the research in it. We will email the address on the account at
                        least {DORMANT_NOTICE_DAYS} days before that happens, and signing
                        in is enough to stop it. Research you published stays readable
                        while the account is live; if the account goes, so does the
                        published copy.
                      </p>
                    </Section>

                    <Section title="Monthly allowances">
                      <p className="text-muted mb-0">
                        Each plan includes a number of research prompts per calendar
                        month. The allowance resets on the first and does not carry over,
                        and unused prompts have no cash value. Cancelling part-way
                        through a month leaves the allowance you have already paid for
                        available until that month ends.
                      </p>
                    </Section>

                    <Section title="How payments are handled">
                      <p className="text-muted mb-0">
                        Payments are processed by Stripe, which acts as the
                        merchant of record and is the seller for the transaction. Any
                        refund we do agree to is issued through them and returned to the
                        original payment method. Card details are never sent to or
                        stored by us.
                      </p>
                    </Section>

                    <Section title="Your statutory rights">
                      <p className="text-muted">
                        Nothing on this page limits rights you have under the consumer
                        protection law of the country you live in. Where that law gives
                        you a stronger right than this policy does, that right applies
                        and this policy gives way to it.
                      </p>
                      <p className="text-muted mb-0">
                        Consumers in the EU and the UK, in particular, have a statutory
                        right to withdraw from a purchase of digital services within 14
                        days. If that right applies to you, you have it regardless of
                        anything written above — write to us and say so.
                      </p>
                    </Section>

                    <div className="mt-4 pt-3 border-top">
                      <ResearchDisclaimer />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
        <Footer />
      </div>
    </React.Fragment>
  );
};

export default RefundPolicy;
